// BuildingAccessors.ts — the mobx-graphlink accessor layer for the Buildings Set1 modifier system
// (§3.1–3.3). These are reactive: any relevant DB change re-runs them. They return plain data ONLY
// (no `three` imports — geometry construction lives in the renderer layer §6). The pure fallback
// merge engine lives in `Building/BuildingDefaults_Pure.ts` (Node-testable without mobx-graphlink).
import {CreateAccessor} from "mobx-graphlink";
import {MapObject} from "./@MapObject.js";
import {GetMapObject, GetMapObjects} from "../mapObjects.js";
import {GetObjAncestors, GetObjDescendants} from "./$mapObject.js";
import {ObjModifier} from "./@MapObject/ObjModifier.js";
import {Mod_Building} from "./@MapObject/ObjModifiers/Mod_Building.js";
import {Mod_Level} from "./@MapObject/ObjModifiers/Mod_Level.js";
import {Mod_Room} from "./@MapObject/ObjModifiers/Mod_Room.js";
import {BuildingFallbackFor} from "./@MapObject/ObjModifiers/Mod_Building.js";
import {
	MergeBuildingDefaultsChain,
	MapModifierToDefaultsSource,
	type BuildingDefaultsSource,
} from "./Building/BuildingDefaults_Pure.js";
import type {ResolvedBuildingDefaults} from "./@MapObject/ObjModifiers/Building/BuildingDefaults_Hardcoded.js";

/** The enabled modifier of a given key on `obj`, or null. Cheap non-accessor helper. */
export function GetEnabledModifier(obj: MapObject, key: keyof ObjModifier): ObjModifier|n {
	for (const mod of obj.modifiers ?? []) {
		if (mod.enabled && mod[key] != null) return mod;
	}
	return null;
}
export function GetEnabledMod<K extends keyof ObjModifier>(obj: MapObject, key: K): ObjModifier[K]|n {
	const wrapper = GetEnabledModifier(obj, key);
	return wrapper ? wrapper[key] : null;
}

// ---------------------------------------------------------------------------
// §3.1 Context lookup
// ---------------------------------------------------------------------------
export type RoomOwner = {obj: MapObject; mod: Mod_Room};
export type LevelOwner = {obj: MapObject; mod: Mod_Level};
export type BuildingOwner = {obj: MapObject; mod: Mod_Building};

/** The closest ancestor-or-self object carrying an enabled `Mod_Room`. */
export const FindOwningRoom = CreateAccessor((obj: MapObject): RoomOwner|n=>{
	for (const anc of [...GetObjAncestors(obj), obj].Reversed()) {
		const mod = GetEnabledMod(anc, "room");
		if (mod) return {obj: anc, mod};
	}
	return null;
});

/** The closest ancestor-or-self object carrying an enabled `Mod_Level`. */
export const FindOwningLevel = CreateAccessor((obj: MapObject): LevelOwner|n=>{
	for (const anc of [...GetObjAncestors(obj), obj].Reversed()) {
		const mod = GetEnabledMod(anc, "level");
		if (mod) return {obj: anc, mod};
	}
	return null;
});

/** Closest ancestor-or-self with an enabled, NON-prototype `Mod_Building`. */
export const FindOwningBuilding = CreateAccessor((obj: MapObject): BuildingOwner|n=>{
	for (const anc of [...GetObjAncestors(obj), obj].Reversed()) {
		const mod = GetEnabledMod(anc, "building");
		if (mod && mod.fallbackFor == null) return {obj: anc, mod};
	}
	return null;
});

// ---------------------------------------------------------------------------
// §3.2 Prototype lookup
// ---------------------------------------------------------------------------
/** Nearest ancestor-or-self Building mod with `fallbackFor == "descendants"`. */
export const FindDescendantsPrototypeBuilding = CreateAccessor((obj: MapObject): Mod_Building|n=>{
	for (const anc of [...GetObjAncestors(obj), obj].Reversed()) {
		const mod = GetEnabledMod(anc, "building");
		if (mod && mod.fallbackFor == BuildingFallbackFor.descendants) return mod;
	}
	return null;
});

/** Map-wide scan (mirroring `GetTerrainMaterialsMods`) for a Building mod with
 *  `fallbackFor == "global"`; if several exist, lowest orderKey wins (and the UI warns). */
export const FindGlobalPrototypeBuilding = CreateAccessor((mapID: string): Mod_Building|n=>{
	let best: {obj: MapObject; mod: Mod_Building}|n = null;
	for (const obj of GetMapObjects(mapID)) {
		const mod = GetEnabledMod(obj, "building");
		if (mod && mod.fallbackFor == BuildingFallbackFor.global) {
			if (best == null) { best = {obj, mod}; continue; }
			// lowest orderKey wins
			if ((obj.orderKey ?? "") < (best.obj.orderKey ?? "")) best = {obj, mod};
		}
	}
	return best?.mod ?? null;
});

// ---------------------------------------------------------------------------
// §3.3 The generic chain
// ---------------------------------------------------------------------------
/** Ordered nearest-first list of defaults sources for `obj`. The chain bottoms out at the
 *  hardcoded defaults, so `MergeBuildingDefaultsChain` always fully resolves. */
export const ResolveBuildingDefaultsChain = CreateAccessor((obj: MapObject): BuildingDefaultsSource[]=>{
	const sources: BuildingDefaultsSource[] = [];

	// self Room/Window/Door mods, mapped into the BuildingDefaults shape
	for (const wrapper of obj.modifiers ?? []) {
		const mapped = MapModifierToDefaultsSource(wrapper);
		if (mapped) sources.push(mapped);
	}

	const levelOwner = FindOwningLevel(obj);
	if (levelOwner) sources.push({label: "Level", defaults: {
		level: {floorThickness: levelOwner.mod.floorThickness, wallHeight: levelOwner.mod.wallHeight},
		// Set2 §4.1: the owning level is a source for `room.wallThickness` too, so a room that
		// declares nothing still inherits its floor's wall thickness.
		room: {wallThickness: levelOwner.mod.wallThickness},
		materials: levelOwner.mod.materials,
	}});

	const buildingOwner = FindOwningBuilding(obj);
	if (buildingOwner) {
		sources.push({label: "Building", defaults: buildingOwner.mod.defaults});
	}

	const descendantsProto = FindDescendantsPrototypeBuilding(obj);
	if (descendantsProto) sources.push({label: "Building prototype", defaults: descendantsProto.defaults});

	const globalProto = FindGlobalPrototypeBuilding(obj.map);
	if (globalProto) sources.push({label: "Global prototype", defaults: globalProto.defaults});

	return sources;
});

/** Deep per-leaf first-non-null merge over the §3.3 chain. Returns a fully-populated
 *  `ResolvedBuildingDefaults` plus per-leaf provenance for the UI. */
export const ResolveBuildingDefaults = CreateAccessor((obj: MapObject): {
	values: ResolvedBuildingDefaults;
	provenance: {[leafPath: string]: string};
}=>{
	return MergeBuildingDefaultsChain(ResolveBuildingDefaultsChain(obj));
});

// ---------------------------------------------------------------------------
// Slice convenience wrappers
// ---------------------------------------------------------------------------
export const ResolveLevelParams = CreateAccessor((obj: MapObject): ResolvedBuildingDefaults["level"]=>{
	const chain = ResolveBuildingDefaultsChain(obj);
	// A room-less object (a bare Level) should still resolve its own level slice.
	return MergeBuildingDefaultsChain(chain).values.level;
});
export const ResolveRoomParams = CreateAccessor((obj: MapObject): ResolvedBuildingDefaults["room"]=>{
	return MergeBuildingDefaultsChain(ResolveBuildingDefaultsChain(obj)).values.room;
});
export const ResolveWindowParams = CreateAccessor((obj: MapObject): ResolvedBuildingDefaults["window"]=>{
	return MergeBuildingDefaultsChain(ResolveBuildingDefaultsChain(obj)).values.window;
});
export const ResolveDoorParams = CreateAccessor((obj: MapObject): ResolvedBuildingDefaults["door"]=>{
	return MergeBuildingDefaultsChain(ResolveBuildingDefaultsChain(obj)).values.door;
});
export const ResolveOpeningParams = CreateAccessor((obj: MapObject): ResolvedBuildingDefaults["opening"]=>{
	return MergeBuildingDefaultsChain(ResolveBuildingDefaultsChain(obj)).values.opening;
});
export const ResolveRoofParams = CreateAccessor((obj: MapObject): ResolvedBuildingDefaults["roof"]=>{
	return MergeBuildingDefaultsChain(ResolveBuildingDefaultsChain(obj)).values.roof;
});
export const ResolveBuildingMaterials = CreateAccessor((obj: MapObject): ResolvedBuildingDefaults["materials"]=>{
	return MergeBuildingDefaultsChain(ResolveBuildingDefaultsChain(obj)).values.materials;
});
